import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocalStorageState } from '@components/component-helpers';
import { ColumnSort } from '@components/data-grid';
import { Filters, useFilters } from '@components/filters';
import type { GetConjunctionProps } from '@components/filters';
import { Pagination, usePagination } from '@components/pagination';
import { Stack } from '@components/stack';
import useQueryParams from '@hooks/use-query-params';
import { storage } from '@utils/browser';
import { toQueryString } from '@utils/requests';

import { api } from 'api';
import { Box } from 'components/Box';
import { Loading } from 'components/Loading';
import { AutoParentScrollPageContent } from 'components/Page';
import { useFilterStorageKey } from 'features/filters/helpers';
import { useStore } from 'models/Provider';
import type { OnboardApplication } from 'types/applications';

import {
  mapKeyToSortKey,
  OnboardingProvider,
  pageSize,
  useOnboardingFilterState,
  useOnboardingFilterConfig,
} from './onboarding-config';
import { OnboardingHeader } from './onboarding-header';
import { OnboardingList } from './onboarding-list';

export const OnboardingPage = () => {
  const { t } = useStore();
  const [count, setCount] = useState(0);

  const search = useOnboardingFilterState((state) => state.debouncedSearch);
  const clearSearch = useOnboardingFilterState((state) => state.clear);
  const section = useQueryParams('section');
  const filterStorageKey = useFilterStorageKey('onboarding');

  const filters = useFilters({
    filterConfigs: useOnboardingFilterConfig(),
    storageKey: filterStorageKey,
    onFilterClear: clearSearch,
  });

  const { numPages, selectedPage, selectPage } = usePagination({
    pageSize,
    defaultPage: Number(storage.get('onboarding-page')) || 1,
    count,
  });

  useEffect(() => {
    storage.set('onboarding-page', String(selectedPage));
  }, [selectedPage]);

  const [sort, setSort] = useLocalStorageState<ColumnSort<OnboardApplication>>({
    key: 'global-onboarding-sort',
    defaultValue: {
      key: 'date',
      order: 'descending',
    },
  });

  const isCompletedSection = section === 'completed';

  const queryString = useMemo(() => {
    return toQueryString({
      page_size: pageSize,
      page: selectedPage,
      ...filters.queryData,
      search_multi_match: search || null,
      ordering: `${sort.order === 'descending' ? '-' : ''}${mapKeyToSortKey[sort.key] || sort.key}`,
    });
  }, [filters.queryData, search, selectedPage, sort.key, sort.order]);

  const onboardingQuery = api.applications.useOnboardList({
    isCompleted: isCompletedSection,
    queryString,
    onSuccess: (data) => setCount(data.count),
  });

  const onboardingList = onboardingQuery.data?.results ?? [];
  const onboardingCount = onboardingQuery.data?.count ?? 0;

  const totalCountQuery = api.applications.useTotalCount(
    isCompletedSection ? 'onboarded-count' : 'onboarding-count',
  );
  const totalCount = totalCountQuery.data ?? 0;

  const getConjunctionProps: GetConjunctionProps = useCallback(
    () => ({
      allowSwitch: false,
      label: t('filters.conj.and'),
    }),
    [t],
  );

  const isLoading = onboardingQuery.isLoading || totalCountQuery.isLoading;
  const showingCountText = isLoading
    ? t('filters.loading')
    : t('applicants.filters.showing', onboardingCount, totalCount);

  return (
    <OnboardingProvider
      filters={filters}
      sort={sort}
      count={onboardingCount}
      totalCount={totalCount}
    >
      <OnboardingHeader />
      {isLoading && <Loading delay={500} />}
      <Filters
        addFilterText={t('filters.add')}
        clearFiltersText={t('filters.clear')}
        getConjunctionProps={getConjunctionProps}
        showingCountText={showingCountText}
        {...filters}
      />
      {onboardingQuery.data && (
        <AutoParentScrollPageContent>
          <Stack space={6} style={{ marginBottom: 80 }}>
            <OnboardingList
              data={onboardingList}
              key={String(filters.state.isOpen)}
              sort={sort}
              onSortChange={setSort}
              isCompletedSection={isCompletedSection}
            />
            <Box pr={24}>
              <Pagination
                justify="right"
                count={numPages}
                selected={selectedPage}
                onSelect={selectPage}
              />
            </Box>
          </Stack>
        </AutoParentScrollPageContent>
      )}
    </OnboardingProvider>
  );
};
